import { memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { rehypeRevealTail } from "./revealTail";

const REMARK_PLUGINS = [remarkGfm];
const REVEAL_PLUGINS = [rehypeRevealTail];

const MD_COMPONENTS: Components = {
          h1: ({ children }) => (
            <h1 className="mb-2 mt-4 text-[0.95rem] font-semibold tracking-tight first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-2 mt-4 text-[0.95rem] font-semibold tracking-tight first:mt-0">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-1.5 mt-3 text-sm font-semibold tracking-tight first:mt-0">{children}</h3>
          ),
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0 marker:text-primary/60">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0 marker:text-muted-foreground">{children}</ol>
          ),
          li: ({ children }) => <li className="pl-1 [&>ol]:mt-1 [&>ul]:mt-1">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary underline underline-offset-2"
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="rounded-sm border border-border/60 bg-muted px-1.25 py-0.5 font-mono text-[0.8em] text-foreground">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="mb-2 overflow-x-auto rounded-xl border border-border/60 bg-muted p-3 text-[0.8em] last:mb-0 [&_code]:border-0 [&_code]:bg-transparent [&_code]:p-0">
              {children}
            </pre>
          ),
          hr: () => <hr className="my-3 border-border/70" />,
          blockquote: ({ children }) => (
            <blockquote className="mb-2 border-l-2 border-primary/40 pl-3 text-muted-foreground last:mb-0">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="mb-2 overflow-x-auto rounded-lg border border-border last:mb-0">
              <table className="w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
          th: ({ children }) => (
            <th className="border-b border-border px-2.5 py-1.5 text-left font-semibold">{children}</th>
          ),
  td: ({ children }) => (
    <td className="border-b border-border/60 px-2.5 py-1.5">{children}</td>
  ),
};

/**
 * Renders Amy's markdown replies with styling tuned for the chat surface.
 * Memoized on the content string so completed messages/parts never re-parse
 * while a sibling streams.
 */
export const MarkdownMessage = memo(function MarkdownMessage({
  content,
  streaming = false,
}: {
  content: string;
  /** Still being written: newly arrived words fade in (see revealTail.ts). */
  streaming?: boolean;
}) {
  return (
    <div className="text-sm leading-relaxed wrap-break-word">
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={streaming ? REVEAL_PLUGINS : undefined}
        components={MD_COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
